/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/typedef */
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { Subscription } from 'rxjs';

import { CmsCore, CmsPlanSwitch } from 'store/cms/models';
import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { LineDataPlan } from 'store/line/models';
import { User } from 'store/user/models';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { LineSummary } from 'store/account-summary/models';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
@Component({
    selector: 'xm-plan-switch-review',
    templateUrl: 'plan-switch-review.html',
    styleUrls: ['plan-switch-review.scss'],
    standalone: false
})
export class XmPlanSwitchReview implements OnDestroy, OnInit {
    @Input() public pageData?: CmsPlanSwitchReviewData;
    @Input() public selectedPlanData?: CmsDataPlanDetails;
    @Input() public selectedPlanEligibilityData?: LineDataPlan;
    @Input() public features?: CmsDetailPlanFeature[];
    @Input() public isLevel5Customer?: boolean;
    @Input() public lineSummary?: LineSummary;
    @Input() public enablePlanSevenResi?: boolean;
    @Input() public enablePlanSevenCBM?: boolean;

    @Output() public navigationObject: EventEmitter<boolean> = new EventEmitter<boolean>();

    public isUnlimited: boolean;
    public iconOption: MediaImageOptions;
    public checkmarkIcon: MediaImageOptions;
    public promoResponse: DiscountPromoCollection;
    public isDiscountPromotion: boolean = false;
    public promotionType: string;
    public promoContent: string;
    public promoBYOContent: string;
    public promoUNLContent: string;
    public tradeInDDAPromoContent: string;
    public extendedPromoContent: string;
    public hasSwitchPromoBYO: boolean = false;
    public isIneligibleScheduled: boolean = false;
    public user: User;
    public showBogoPromoLossForUnlLines: boolean = false;
    public isIcopPlanSwitch: boolean = false;
    public isBillPreviewEnabled: boolean = false;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private cimaCore: CimaCore;
    private config: IXMOptions;

    constructor(xmStore: XmStore, state: StateService, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, state, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.cimaCore.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.cimaCore.isBusinessUser);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
        }));

        if (this.pageData?.reviewDetails || this.pageData?.switchNowBtn) {
            this.pageData.switchNowBtn = this.isBillPreviewEnabled? this.pageData.reviewDetails : this.pageData.switchNowBtn;
        }

        if (((!this.user.account.isSegmentFive) || (!this.enablePlanSevenResi && !this.enablePlanSevenCBM)) && !this.isIcopPlanSwitch) {
            this.showBogoPromoLossForUnlLines = this.lineSummary.numberOfLinesInThePlan < 3;

            this.isUnlimited = this.selectedPlanEligibilityData.isUnlimited;

            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
                this.checkmarkIcon = {
                    mobile: Cloudinary.generateMediaFromCms(result.icons.greenCircleCheck)
                };
            }));

            this.isIneligibleScheduled = this.user.account.isSegmentFive && this.selectedPlanEligibilityData?.isIneligibleScheduled;
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<DiscountPromoCollection>(DiscountPromoCollection), (result: DiscountPromoCollection) => {
                this.promoResponse = result;
                this.planSwitchPromoEligible();
            }));

            this.iconOption = {
                mobile: Cloudinary.generateMediaFromCms(this.selectedPlanData.miniIcon)
            };
        }
    }

    public getMediaImageOptions(icon: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(icon)
        };
    }

    /** 
     * This method is designed to dynamically update promotional content and eligibility based on the current plan switch context. 
     * It handles multiple promotion types and ensures that the UI can display the correct promotional information to the user based on their eligibility.
    */
    public planSwitchPromoEligible(): void {
        this.hasSwitchPromoBYO = this.promoResponse?.isLinePromoBYOEligible ?? false;  // performing null check if the promoResponse is not available
        this.isDiscountPromotion = this.promoResponse && (this.promoResponse.isLinePromoEligible || this.promoResponse.isAccountPromoEligible || this.promoResponse.isTradeInDDAPromoEligible || this.promoResponse.isExtendedPromoDollarEligible);
        if (this.isDiscountPromotion) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (resp: CmsPlanSwitch) => {
                const promo: CmsPlanSwitchMultiplePromotions = resp.planSwitchMultiplePromotions;
                this.promoContent = promo.reviewDescription.general;
                this.promoBYOContent = this.promoResponse.isLinePromoBYOEligible ? promo.reviewDescription.BYO : undefined;
                if (this.promoResponse.isLinePromoUNLFREEEligible || this.promoResponse.isLinePromoUNLHL25Eligible) {
                    const servicePromoLine = this.promoResponse.mvnoPromotions.find(promo => promo.type === 'SERVICE_PROMO_LINE' && (promo.lineId === this.lineSummary.id.key || promo.lineId === this.lineSummary.id.value));
                    if (servicePromoLine) {
                        this.promoUNLContent = this.cimaCore.isBusinessUser ? promo.reviewCBMDescription.UNLFREE :promo.reviewDescription.UNLFREE;
                        this.promoUNLContent = this.promoUNLContent.replace('[PROMOTION_TERM]', servicePromoLine.promotionTerm.toString());
                    }
                } else if (this.promoResponse.isAccountPromoEligible) {
                    this.promoUNLContent = promo.reviewDescription.UNL;
                }
                const tradeInDDAPromo: DiscountPromotion[] = this.promoResponse.tradeInDDAPromoEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
                const extendedDDAPromo: DiscountPromotion[] = this.promoResponse.extendedPromoDollarEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);

                if (tradeInDDAPromo.length > 0) {
                    if (this.cimaCore.isBusinessUser) {
                        this.promoContent = promo.reviewCBMDescription.general;
                        this.tradeInDDAPromoContent = promo.reviewCBMDescription.DDA.replace('[PROMO_AMOUNT]', tradeInDDAPromo[0].billerInfo.promotionAmount.toString());
                    } else {
                        this.tradeInDDAPromoContent = promo.reviewDescription.DDA.replace('[PROMO_AMOUNT]', tradeInDDAPromo[0].billerInfo.promotionAmount.toString());
                    }
                }
                if (extendedDDAPromo.length > 0) {
                    if (this.cimaCore.isBusinessUser) {
                        this.promoContent = promo.reviewCBMDescription.general;
                        this.extendedPromoContent = promo.reviewCBMDescription.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo[0].billerInfo.promotionAmount.toString());
                    } else {
                        this.extendedPromoContent = promo.reviewDescription.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo[0].billerInfo.promotionAmount.toString());
                    }
                }
            }));
        }
    }

    public onCancel(): void {
        this.navigationObject.emit(false);
    }

    public navToSwitchInProgress(): void {
        this.navigationObject.emit(true);
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }
}
